import React, { useState } from "react";
import { Grid, Typography, useTheme } from "@mui/material";
import { durationDisplay } from "../../../../SoundClipEditor/Controls/Player";
import { Observer, observer } from "mobx-react";
import EditIcon from "@mui/icons-material/Edit";
import { SpeakerPopover } from "./SpeakerPopover";
import { Blurb } from "./Blurb";
import { Speaker } from "../../Stores/SpeakerStore/constants";
import { SpeakerStore } from "../../Stores/SpeakerStore/SpeakerStore";
import { getSpeakerColorByChannelOrPersona } from "Theme/utils";
import { sxClsx } from "utils/styleClsx";

interface BlurbBoxNameDropdownProps {
    blurb: Blurb;
    speakerStore?: SpeakerStore;
    isDisabled?: boolean;
}

export const BlurbBoxSpeakerTitle: React.FC<BlurbBoxNameDropdownProps> =
    observer(({ blurb, speakerStore, isDisabled }) => {
        const disabled = !speakerStore?.amdId || isDisabled;
        const theme = useTheme();
        const [anchorEl, setAnchorEl] = useState<HTMLSpanElement | null>(null);
        const [isEditing, setisEditing] = useState(false);
        const [isHovering, setIsHovering] = useState(false);

        return (
            <Grid
                className={"highlightIgnore"}
                sx={{
                    WebkitTouchCallout: "none",
                    WebkitUserSelect: "none",
                    KhtmlUserSelect: "none",
                    MozUserSelect: "none",
                    MsUserSelect: "none",
                    UserSelect: "none",
                    width: "100px",
                    minWidth: "100px",
                    flexDirection: "column",
                    display: "flex",
                }}
                onMouseDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                }}
                onMouseUp={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                }}
            >
                <span
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (!disabled) {
                            setisEditing(true);
                            setAnchorEl(e.currentTarget);
                        }
                    }}
                    style={{ position: "relative" }}
                    onMouseEnter={(e) => {
                        const actions =
                            e.currentTarget.querySelector(".hiddenActions");
                        if (actions)
                            (actions as HTMLElement).style.visibility =
                                "visible";
                    }}
                    onMouseLeave={(e) => {
                        const actions =
                            e.currentTarget.querySelector(".hiddenActions");
                        if (actions)
                            (actions as HTMLElement).style.visibility =
                                "hidden";
                    }}
                >
                    <Typography
                        className="highlightIgnore"
                        sx={{
                            alignItems: "center",
                            fontWeight: "bold",
                            color: getSpeakerColorByChannelOrPersona(
                                blurb.channel,
                            ),
                            padding: theme.spacing(0.25),
                            paddingRight: "4px",
                            paddingLeft: "4px",
                            "&:hover": disabled
                                ? {}
                                : {
                                      border: `2px ${theme.palette.gray[200]} solid`,
                                      paddingLeft: "2px",
                                      cursor: "default",
                                      borderRadius: 1,
                                      backgroundColor: theme.palette.white.main,
                                  },
                        }}
                        component="span"
                        onMouseOver={(e) => {
                            if (!disabled) {
                                setIsHovering(true);
                            }
                        }}
                        onMouseOut={() => {
                            if (!disabled) {
                                setIsHovering(false);
                            }
                        }}
                    >
                        <Observer>
                            {() => (
                                <>
                                    {speakerStore?.speakerChannelMap.get(
                                        blurb.channel,
                                    )?.name ??
                                        speakerStore?.predictedSpeakerChannelMap.get(
                                            blurb.channel,
                                        ) ??
                                        `Speaker ${blurb.channel + 1}`}
                                </>
                            )}
                        </Observer>
                        <EditIcon
                            className="hidden-edit-icon"
                            fontSize="inherit"
                            sx={sxClsx(
                                {
                                    color: getSpeakerColorByChannelOrPersona(
                                        blurb.channel,
                                    ),
                                },
                                disabled && { visibility: "hidden" },
                                !disabled && {
                                    visibility:
                                        isHovering || isEditing
                                            ? "visible"
                                            : "hidden",
                                },
                            )}
                        />
                    </Typography>
                </span>
                <SpeakerPopover
                    open={isEditing && !disabled}
                    anchorEl={anchorEl}
                    blurb={blurb}
                    onClose={() => {
                        setisEditing(false);
                        setAnchorEl(null);
                    }}
                    onSetSpeaker={(speaker: Speaker) => {
                        speakerStore?.setSpeakerNameByChannel(
                            blurb.channel,
                            speaker,
                        );
                        setisEditing(false);
                        setAnchorEl(null);
                    }}
                />

                <Typography
                    sx={{
                        color: theme.palette.gray[400],
                        padding: theme.spacing(0.25),
                        paddingLeft: "4px",
                    }}
                    className="highlightIgnore"
                >
                    {durationDisplay(blurb.timeInterval[0])}
                </Typography>
            </Grid>
        );
    });
